import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { generateNavigationMenu } from 'App/Map/LayerNav/Layers/generateNavigationMenu/generateNavigationMenu'
import { useMemo, useRef } from 'react'
import { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { capitalizeFirstLetter } from 'utils/capitalizeFirstLetter'
import { useDeepCompareMemo } from 'utils/useDeepCompareMemo'
import { SectionTable } from '../../App/Map/PanelDetails/OverviewCharts/SectionTable/SectionTable'

export const OverviewGridTable = ({
  containerWidth,
  data,
  timestamp,
  isLoading,
}: {
  containerWidth: number
  data: routes.ValuesCurrent.Response
  timestamp: string
  isLoading: boolean
}) => {
  const gridTableRef = useRef<HTMLDivElement>(null)
  const selectedValueGroups = selectedValueGroupsStore.getState().selectedValueGroups
  const navigationMenu = generateNavigationMenu({ selectedValueGroups })
  const specialKeys = ['conditions', 'grape_powdery_mildew', 'cherry_powdery_mildew']

  const layersActive = useMemo(() => {
    return navigationMenu
      .filter((section) => {
        // Only consider sections with SOME/ALL checked children
        if (section.childrenChecked !== 'SOME' && section.childrenChecked !== 'ALL') return false

        const checkedChildren = section.children.filter((child) => child.checked)

        // If the only checked value is in specialKeys, exclude this layer
        if (checkedChildren.length === 1 && specialKeys.includes(checkedChildren[0].key)) {
          return false
        }

        return true
      })
      .map((section) =>
        section.key === 'insects' ? 'Pests' : section.key.split('-').map(capitalizeFirstLetter).join(' '),
      )
      .filter((label) => label !== 'Equipment')
  }, [navigationMenu])

  const valuesToOmit = ['wind_machine', 'equipment_status', ...specialKeys]

  const { selectedValueGroupsArray } = useMemo(() => {
    const flatMapValueGroups = (valueGroups: typeof navigationMenu) =>
      valueGroups
        .flatMap((section) =>
          section.children.map((valueGroup) => ({
            label: valueGroup.translatedTitle,
            value: valueGroup.key,
            checked: valueGroup.checked,
          })),
        )
        .sort((a, b) => a.label.localeCompare(b.label))

    const valueGroupOptions = flatMapValueGroups(navigationMenu).filter(
      (valueGroup) => !valuesToOmit.includes(valueGroup.value),
    )

    const selectedValueGroupsArray = valueGroupOptions.filter((valueGroup) => valueGroup.checked === true)

    return { selectedValueGroupsArray }
  }, [navigationMenu])

  // the navigation menu is new on every render, and SectionTable rebuilds all its data when these change identity
  const stableLayersActive = useDeepCompareMemo(layersActive)
  const stableSelectedValueGroupsArray = useDeepCompareMemo(selectedValueGroupsArray)

  return (
    <div
      css={{
        display: 'flex',
        overflow: 'hidden',
        maxHeight: '100%',
        transition: '200ms ease all',
        flexDirection: 'column',
      }}
    >
      <div ref={gridTableRef} css={{ overflowX: 'auto', minWidth: `${containerWidth}px` }}>
        <SectionTable
          data={data}
          selectedValueGroupsArray={stableSelectedValueGroupsArray}
          layersActive={stableLayersActive}
          timestamp={timestamp}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
